import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { GET_MEETING_BY_ID } from '../../graphql/ql/schema';
import { getPlayableUrl, isVideoUrl } from '../../utils/mediaUrl';
import { callAxiosServiceWithParam } from '../../services/callAxios';
import styles from './Mobile.module.css';

interface MobileMeetingPlayerProps {
    meetingId: string;
    onBack: () => void;
}

const PLAYBACK_RATES = [0.75, 1.0, 1.25, 1.5, 2.0];

function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '';
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins} min`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hrs}h ${remainMins}m`;
}

const MobileMeetingPlayer: React.FC<MobileMeetingPlayerProps> = ({ meetingId, onBack }) => {
    const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
    const [signedUrl, setSignedUrl] = useState<string | null>(null);
    const [isVideo, setIsVideo] = useState(true);
    const [isPlaying, setIsPlaying] = useState(false);
    const [rateIndex, setRateIndex] = useState(1); // Default 1.0x
    const [urlError, setUrlError] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const { loading, error, data } = useQuery(GET_MEETING_BY_ID, {
        variables: { id: meetingId },
        fetchPolicy: 'cache-and-network',
    });

    const meeting = data?.meeting;

    const fetchSignedUrl = useCallback(async (rawUrl: string) => {
        try {
            const response = await callAxiosServiceWithParam(
                'users/signedurl',
                'filepath=' + encodeURIComponent(rawUrl)
            );
            setSignedUrl(response.data);
        } catch (e) {
            console.error('Failed to get signed URL:', e);
            setUrlError(true);
        }
    }, []);

    useEffect(() => {
        if (meeting?.sources && meeting.sources.length > 0) {
            const rawUrl = getPlayableUrl(meeting.sources);
            if (rawUrl) {
                setIsVideo(isVideoUrl(rawUrl));
                fetchSignedUrl(rawUrl);
            } else {
                setUrlError(true);
            }
        }
    }, [meeting, fetchSignedUrl]);

    const togglePlay = () => {
        if (!mediaRef.current) return;
        if (mediaRef.current.paused) {
            mediaRef.current.play();
            setIsPlaying(true);
        } else {
            mediaRef.current.pause();
            setIsPlaying(false);
        }
    };

    const seekBy = (seconds: number) => {
        if (!mediaRef.current) return;
        mediaRef.current.currentTime = Math.max(0, mediaRef.current.currentTime + seconds);
    };

    const cycleRate = () => {
        const nextIndex = (rateIndex + 1) % PLAYBACK_RATES.length;
        setRateIndex(nextIndex);
        if (mediaRef.current) {
            mediaRef.current.playbackRate = PLAYBACK_RATES[nextIndex];
        }
    };

    const handleMediaPlay = () => setIsPlaying(true);
    const handleMediaPause = () => setIsPlaying(false);
    const handleTimeUpdate = () => {
        if (mediaRef.current) setCurrentTime(mediaRef.current.currentTime);
    };
    const handleLoadedMetadata = () => {
        if (mediaRef.current) setDuration(mediaRef.current.duration);
    };
    const handleProgressSeek = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!mediaRef.current || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        mediaRef.current.currentTime = ratio * duration;
    };

    const formatTime = (secs: number): string => {
        const m = Math.floor(secs / 60);
        const s = Math.floor(secs % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };
    const handleMediaError = () => {
        // Signed URL may have expired — try re-fetching
        if (meeting?.sources && meeting.sources.length > 0) {
            const rawUrl = getPlayableUrl(meeting.sources);
            if (rawUrl) {
                setSignedUrl(null);
                fetchSignedUrl(rawUrl);
            }
        }
    };

    if (loading && !meeting) {
        return (
            <div className={styles.playerContainer}>
                <button className={styles.playerBackButton} onClick={onBack}>
                    ← Back
                </button>
                <div className={styles.playerLoading}>
                    <div className={styles.spinner} />
                    <span>Loading meeting...</span>
                </div>
            </div>
        );
    }

    if (error && !meeting) {
        return (
            <div className={styles.playerContainer}>
                <button className={styles.playerBackButton} onClick={onBack}>
                    ← Back
                </button>
                <div className={styles.errorText}>Unable to load meeting. Please try again.</div>
            </div>
        );
    }

    if (!meeting) return null;

    const noRecording = !meeting.sources || meeting.sources.length === 0 || urlError;

    return (
        <div className={styles.playerContainer}>
            <button className={styles.playerBackButton} onClick={onBack}>
                ← Back to Meetings
            </button>

            {noRecording && !signedUrl ? (
                <div className={styles.noRecording}>
                    <p>Recording not available for this meeting.</p>
                </div>
            ) : !signedUrl ? (
                <div className={styles.playerLoading}>
                    <div className={styles.spinner} />
                    <span>Loading recording...</span>
                </div>
            ) : (
                <>
                    <div className={styles.videoWrapper} onClick={togglePlay}>
                        {isVideo ? (
                            <video
                                ref={mediaRef as React.RefObject<HTMLVideoElement>}
                                src={signedUrl}
                                playsInline
                                onPlay={handleMediaPlay}
                                onPause={handleMediaPause}
                                onError={handleMediaError}
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={handleLoadedMetadata}
                                style={{ width: '100%', display: 'block' }}
                            />
                        ) : (
                            <>
                                <div className={styles.audioPlaceholder}>
                                    <img src="/assets/img/rafiki_blue.svg" alt="Rafiki" />
                                </div>
                                <audio
                                    ref={mediaRef as React.RefObject<HTMLAudioElement>}
                                    src={signedUrl}
                                    onPlay={handleMediaPlay}
                                    onPause={handleMediaPause}
                                    onError={handleMediaError}
                                    onTimeUpdate={handleTimeUpdate}
                                    onLoadedMetadata={handleLoadedMetadata}
                                />
                            </>
                        )}
                        {!isPlaying && (
                            <div className={styles.playOverlay}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            </div>
                        )}
                    </div>

                    <div className={styles.controlsWrapper}>
                        <div className={styles.progressContainer}>
                            <span className={styles.progressTime}>{formatTime(currentTime)}</span>
                            <div className={styles.progressBar} onClick={handleProgressSeek}>
                                <div
                                    className={styles.progressFill}
                                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                                />
                            </div>
                            <span className={styles.progressTime}>{formatTime(duration)}</span>
                        </div>

                        <div className={styles.playerControls}>
                            <button className={styles.controlButton} onClick={() => seekBy(-10)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12.5 3C7.81 3 4.01 6.54 3.56 11H1l3.5 4 3.5-4H5.57c.45-3.38 3.33-6 6.93-6 3.87 0 7 3.13 7 7s-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C7.88 19.84 10.07 21 12.5 21c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/>
                                </svg>
                                <span className={styles.skipLabel}>10</span>
                            </button>
                            <button
                                className={`${styles.controlButton} ${styles.playPauseButton}`}
                                onClick={togglePlay}
                            >
                                {isPlaying ? (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <rect x="6" y="4" width="4" height="16" rx="1" />
                                        <rect x="14" y="4" width="4" height="16" rx="1" />
                                    </svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                )}
                            </button>
                            <button className={styles.controlButton} onClick={() => seekBy(10)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ transform: 'scaleX(-1)' }}>
                                    <path d="M12.5 3C7.81 3 4.01 6.54 3.56 11H1l3.5 4 3.5-4H5.57c.45-3.38 3.33-6 6.93-6 3.87 0 7 3.13 7 7s-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C7.88 19.84 10.07 21 12.5 21c4.97 0 9-4.03 9-9s-4.03-9-9-9z"/>
                                </svg>
                                <span className={styles.skipLabel}>10</span>
                            </button>
                            <button className={`${styles.controlButton} ${styles.speedButton}`} onClick={cycleRate}>
                                {PLAYBACK_RATES[rateIndex]}x
                            </button>
                        </div>
                    </div>
                </>
            )}

            <div className={styles.meetingDetail}>
                <div className={styles.meetingDetailTitle}>{meeting.title || 'Untitled Meeting'}</div>
                <div className={styles.meetingDetailMeta}>
                    {formatDate(meeting.date)}
                    {meeting.duration ? ` · ${formatDuration(meeting.duration)}` : ''}
                </div>
                {meeting.host && (
                    <div className={styles.meetingDetailMeta}>
                        Hosted by {meeting.host}
                    </div>
                )}

                {meeting.attendees && meeting.attendees.length > 0 && (
                    <div className={styles.meetingDetailAttendees}>
                        <h4>Attendees ({meeting.attendees.length})</h4>
                        <ul className={styles.attendeeList}>
                            {meeting.attendees.map((att: any, i: number) => (
                                <li key={i}>{att.name?.trim() || att.email?.trim() || att.phone?.trim() || 'Unknown'}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileMeetingPlayer;
